import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  AlertTitle,
  CircularProgress,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { useApp } from '../../context/AppContext';
import { HM_OWN_API_BASE, HM_OWN_API_KEY, HM_HOME_CODE_KEY, HM_MOBILE_PHONE_KEY } from '../shared/AppConnectPage';
import { SELECTED_APP_ID_KEY } from '../shared/SelectAppPage';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getApiUrl } from '../../utils/apiUrl';

const ReporterCreatePage = () => {
  const { currentUser } = useApp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const deptIdParam = searchParams.get('dept');

  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    departmentId: null,
    address: '',
    location: '',
    telephone: '',
    urgency: 'normal',
    images: [],
    imageFiles: [],
  });
  const [createAlert, setCreateAlert] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(true);
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [authVerifyUser, setAuthVerifyUser] = useState(null);

  const getStoredAddress = () => (typeof localStorage !== 'undefined' ? localStorage.getItem(HM_HOME_CODE_KEY) || '' : '');
  const hasStoredAddress = () => Boolean(getStoredAddress());
  const getStoredMobilePhone = () => (typeof localStorage !== 'undefined' ? localStorage.getItem(HM_MOBILE_PHONE_KEY) || '' : '');

  useEffect(() => {
    const token = localStorage.getItem('token');
    const url = getApiUrl('/api/user/auth/verify/');
    if (!url || !token) {
      setAuthVerifyUser(null);
      return;
    }
    axios.get(url, { headers: { Authorization: `Token ${token}` } })
      .then(({ data }) => setAuthVerifyUser(data))
      .catch(() => setAuthVerifyUser(null));
  }, []);

  useEffect(() => {
    const selectedAppId = localStorage.getItem(SELECTED_APP_ID_KEY);
    const appsBase = getApiUrl('/api/clients/apps/').replace(/\/$/, '');
    if (!selectedAppId || !appsBase) {
      setDepartments([]);
      setDepartmentsLoading(false);
      return;
    }
    setDepartmentsLoading(true);
    const url = `${appsBase}/${selectedAppId}/departments/`;
    axios.get(url)
      .then(({ data }) => {
        const list = data?.results ?? (Array.isArray(data) ? data : []);
        setDepartments(list.map((d) => ({ id: d.id, name: d.name || '', description: d.description || '' })));
      })
      .catch(() => setDepartments([]))
      .finally(() => setDepartmentsLoading(false));
  }, []);

  useEffect(() => {
    if (!deptIdParam || !departments.length) return;
    const dept = departments.find((d) => d.id === Number(deptIdParam));
    if (!dept) return;
    setSelectedDepartment(dept);
    setFormData((prev) => {
      if (prev.departmentId === Number(deptIdParam)) return prev;
      return {
        title: '',
        description: '',
        departmentId: Number(deptIdParam),
        address: getStoredAddress(),
        location: '',
        telephone: getStoredMobilePhone(),
        urgency: 'normal',
        images: [],
        imageFiles: [],
      };
    });
  }, [deptIdParam, departments]);

  useEffect(() => {
    if (!selectedDepartment || !currentUser?.username) return;
    let cancelled = false;
    const url = `${HM_OWN_API_BASE}/${currentUser.username}/hm_own_api`;
    axios
      .post(url, { key: HM_OWN_API_KEY })
      .then(({ data }) => {
        if (cancelled) return;
        const homeCode = data?.home?.home_code;
        const mobilePhone = data?.owner?.MobilePhone ?? '';
        if (typeof localStorage !== 'undefined') {
          if (homeCode) localStorage.setItem(HM_HOME_CODE_KEY, homeCode);
          if (mobilePhone) localStorage.setItem(HM_MOBILE_PHONE_KEY, mobilePhone);
        }
        setFormData((prev) => ({
          ...prev,
          ...(homeCode && { address: homeCode }),
          ...(mobilePhone && { telephone: mobilePhone }),
        }));
      })
      .catch(() => { });
    return () => { cancelled = true; };
  }, [selectedDepartment, currentUser?.username]);

  const imageInputRef = useRef(null);
  const handleImageChange = (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    const maxFiles = 5;
    const maxSizeMB = 10;
    const currentCount = formData.images?.length || 0;
    const remaining = maxFiles - currentCount;
    if (remaining <= 0) {
      setCreateAlert({ severity: 'warning', title: 'แนบรูปเกินจำนวน', text: `แนบรูปได้สูงสุด ${maxFiles} รูป` });
      e.target.value = '';
      return;
    }
    if (files.length > remaining) {
      setCreateAlert({
        severity: 'warning',
        title: 'แนบรูปเกินจำนวน',
        text: `เลือกได้อีก ${remaining} รูป (รวมสูงสุด ${maxFiles} รูป)`,
      });
    }
    const toAdd = Array.from(files).slice(0, remaining);
    toAdd.forEach((file) => {
      if (file.size > maxSizeMB * 1024 * 1024) {
        setCreateAlert({ severity: 'warning', title: 'ไฟล์ใหญ่เกินขนาด', text: `ไฟล์ ${file.name} ใหญ่เกิน ${maxSizeMB} MB` });
        return;
      }
      const reader = new FileReader();
      reader.onload = (ev) => {
        setFormData((prev) => ({
          ...prev,
          images: [...(prev.images || []), { name: file.name, dataUrl: ev.target.result }],
          imageFiles: [...(prev.imageFiles || []), file],
        }));
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };
  const handleRemoveImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: (prev.images || []).filter((_, i) => i !== index),
      imageFiles: (prev.imageFiles || []).filter((_, i) => i !== index),
    }));
  };

  const handleCreateHelpDesk = async () => {
    setCreateAlert(null);
    if (!formData.title || !formData.description || !formData.location) {
      setCreateAlert({ severity: 'warning', title: 'ข้อมูลไม่ครบ', text: 'กรุณากรอกข้อมูลให้ครบถ้วน' });
      return;
    }
    const departmentId = formData.departmentId;
    if (departmentId == null) {
      setCreateAlert({ severity: 'warning', title: 'ไม่พบหน่วยงาน', text: 'กรุณาลองใหม่อีกครั้งหรือติดต่อผู้ดูแลระบบ' });
      return;
    }
    const ticketsBase = getApiUrl('/api/tickets/');
    if (!ticketsBase) {
      setCreateAlert({ severity: 'error', title: 'ผิดพลาด', text: 'ไม่พบ URL สำหรับสร้าง ticket' });
      return;
    }
    const ownerId = authVerifyUser?.id != null ? Number(authVerifyUser.id) : (currentUser?.id != null ? Number(currentUser.id) : null);
    if (ownerId == null || Number.isNaN(ownerId)) {
      setCreateAlert({ severity: 'warning', title: 'ไม่พบผู้ใช้', text: 'กรุณาเข้าสู่ระบบใหม่' });
      return;
    }

    setCreateSubmitting(true);
    setCreateAlert(null);
    try {
      const postUrl = ticketsBase.endsWith('/') ? ticketsBase : `${ticketsBase}/`;
      const { data: ticket } = await axios.post(postUrl, {
        title: formData.title,
        description: formData.description,
        department: departmentId,
        location: formData.location,
        address: formData.address ?? '',
        telephone: formData.telephone ?? '',
        urgency: formData.urgency ?? 'normal',
        owner: ownerId,
        status: 'pending',
      });
      const ticketId = ticket?.id;
      if (ticketId == null) {
        setCreateAlert({ severity: 'error', title: 'สร้าง ticket ไม่สำเร็จ', text: 'ไม่มี id จาก server' });
        return;
      }

      const attachmentUrl = ticketsBase.endsWith('/') ? `${ticketsBase}attachments/` : `${ticketsBase}/attachments/`;
      const imageFiles = formData.imageFiles || [];
      for (let i = 0; i < imageFiles.length; i++) {
        const file = imageFiles[i];
        const fd = new FormData();
        fd.append('ticket', ticketId);
        fd.append('file', file, file.name || `image-${i + 1}`);
        fd.append('name', file.name || `image-${i + 1}`);
        await axios.post(attachmentUrl, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      navigate('/', { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'เกิดข้อผิดพลาด';
      setCreateAlert({ severity: 'error', title: 'สร้างรายการไม่สำเร็จ', text: String(msg) });
    } finally {
      setCreateSubmitting(false);
    }
  };

  useEffect(() => {
    if (!deptIdParam) navigate('/', { replace: true });
  }, [deptIdParam, navigate]);

  if (!deptIdParam) return null;

  return (
    <Box sx={{ px: { xs: 2, sm: 0 }, pb: { xs: 3, sm: 0 } }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
        <Box sx={{ width: '100%', maxWidth: 720, display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => { setCreateAlert(null); navigate('/'); }} sx={{ borderRadius: 2, color: '#845EC2', borderColor: 'rgba(132, 94, 194, 0.45)', '&:hover': { borderColor: 'rgba(132, 94, 194, 0.65)' } }}>กลับ</Button>
        </Box>
        {createAlert && (
          <Alert severity={createAlert.severity} onClose={() => setCreateAlert(null)} sx={{ width: '100%', maxWidth: 720, mb: 2 }}>
            {createAlert.title && <AlertTitle>{createAlert.title}</AlertTitle>}
            {createAlert.text}
          </Alert>
        )}
        {departmentsLoading ? (
          <Card elevation={0} sx={{ width: '100%', maxWidth: 720, borderRadius: 2, p: 4, textAlign: 'center' }}>
            <CircularProgress size={44} thickness={3.6} sx={{ color: '#845EC2' }} />
            <Typography sx={{ mt: 2, color: 'rgba(0,0,0,0.5)' }}>กำลังโหลด...</Typography>
          </Card>
        ) : !selectedDepartment ? (
          <Card elevation={0} sx={{ width: '100%', maxWidth: 720, borderRadius: 2, p: 4, textAlign: 'center' }}>
            <Typography sx={{ color: 'rgba(0,0,0,0.6)' }}>ไม่พบหมวดหมู่</Typography>
            <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/')} sx={{ mt: 2, borderRadius: 2 }}>กลับ</Button>
          </Card>
        ) : (
          <Card elevation={0} sx={{ width: '100%', maxWidth: 720, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', border: '1px solid rgba(0,0,0,0.05)', overflow: 'hidden', bgcolor: 'rgba(255, 255, 255, 0.9)' }}>
            <Box
              sx={{
                background: 'linear-gradient(to right, #8E6CEB, #4092E9)',
                px: 2,
                pt: 1.5,
                pb: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
              }}
            >

              <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', flex: 1 }}>
                แจ้งข้อขัดข้อง - {selectedDepartment?.name || ''}
              </Typography>
            </Box>
            <CardContent sx={{ pt: 3, pb: 2 }}>
              <TextField fullWidth label="หัวข้อ" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} sx={{ mb: 2 }} />
              <TextField fullWidth label="รายละเอียด" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} multiline rows={4} sx={{ mb: 2 }} />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="ticket-priority-label">ความเร่งด่วน</InputLabel>
                <Select
                  labelId="ticket-priority-label"
                  label="ความเร่งด่วน"
                  value={formData.urgency}
                  onChange={(e) => setFormData({ ...formData, urgency: e.target.value })}
                >
                  <MenuItem value="normal">ไม่เร่งด่วน</MenuItem>
                  <MenuItem value="high">เร่งด่วน</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                label="ที่อยู่"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                InputProps={{
                  readOnly: hasStoredAddress() && formData.address === getStoredAddress(),
                  endAdornment: formData.address ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setFormData({ ...formData, address: '' })} edge="end" aria-label="ล้างที่อยู่">
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{ mb: 2 }}
                placeholder="รหัสที่อยู่ (จากระบบ)"
              />
              <TextField fullWidth label="สถานที่เกิดเหตุ" value={formData.location} onChange={(e) => setFormData({ ...formData, location: e.target.value })} sx={{ mb: 2 }} placeholder="เช่น ห้อง 101, อาคาร A" />
              <TextField
                fullWidth
                label="เบอร์ติดต่อ"
                value={formData.telephone}
                onChange={(e) => {
                  const digitsOnly = [...e.target.value].filter((ch) => ch >= '0' && ch <= '9').join('');
                  setFormData({ ...formData, telephone: digitsOnly });
                }}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                sx={{ mb: 2 }}
                placeholder="เช่น 0812345678"
              />
              <Box sx={{ borderTop: '1px solid rgba(0,0,0,0.08)', pt: 2, mt: 0.5, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <AddPhotoAlternateIcon sx={{ color: '#845EC2', fontSize: 22 }} />
                  <Typography variant="subtitle2" sx={{ color: 'rgba(0,0,0,0.7)', fontWeight: 600 }}>แนบรูปภาพ (ไม่เกิน 5 รูป, รูปละไม่เกิน 10 MB)</Typography>
                </Box>
                <input ref={imageInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleImageChange} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start' }}>
                  <Button variant="outlined" component="span" startIcon={<AddPhotoAlternateIcon />} onClick={() => imageInputRef.current?.click()} disabled={(formData.images?.length || 0) >= 5} sx={{ borderStyle: 'dashed', borderColor: 'rgba(132, 94, 194, 0.5)', color: '#845EC2', '&:hover': { borderColor: '#845EC2', bgcolor: 'rgba(132, 94, 194, 0.04)' } }}>อัปโหลดรูป</Button>
                  {(formData.images || []).map((img, i) => (
                    <Box key={i} sx={{ position: 'relative', width: 72, height: 72, borderRadius: 1, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.12)', flexShrink: 0 }}>
                      <img src={img.dataUrl} alt={img.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <IconButton size="small" onClick={() => handleRemoveImage(i)} sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(0,0,0,0.5)', color: 'white', width: 24, height: 24, '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}>
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Box>
            </CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, p: 2, pt: 0 }}>
              <Button onClick={() => navigate('/')} disabled={createSubmitting} sx={{ borderRadius: 2 }} startIcon={<ArrowBackIcon />}>กลับ</Button>
              <Button onClick={handleCreateHelpDesk} variant="contained" disabled={createSubmitting} sx={{ bgcolor: '#845EC2', color: 'white', borderRadius: 2, '&:hover': { bgcolor: '#6344A4' } }}>
                {createSubmitting ? 'กำลังสร้าง...' : 'สร้าง'}
              </Button>
            </Box>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default ReporterCreatePage;
